
import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import { getSelectedSport, isCBB } from '../utils/helpers';
import PlayerChatbot from './player_profiles/PlayerChatbot';
import { useTheme } from '../utils/theme';
import { formatHeight, formatWeight, positionsBaseball, positionsMBB } from '../utils/constant';
import PlayerCardSkeleton from './PlayerCardSkeleton';
import { MessageSquare, Smartphone, Sparkles, X, Loader2, Activity, Zap, TrendingUp, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMBBRoasters } from '../utils/MBBApis';
import { fetchMLBTeamRoster } from '../lib/mlbRosterService';
import { getCBBTeamRoster, isCBBTeam } from '../lib/cbbRosterService';
import { getBasketballPlayers } from '../utils/basketballDataService';
import { evaluatePlayer } from '../lib/geminiService';
import { BiometricBadgesCompact } from './BiometricBadges';

/**
 * Get current academic season for college sports
 */
const getCurrentSeason = () => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    return month >= 7 ? year + 1 : year;
};

/**
 * Check if a player is on the current roster (not a former player)
 */
const isCurrentPlayer = (player, currentSeason) => {
    const playerYear = player?.season || player?.roster_year || player?.year;
    if (!playerYear) {
        const playerClass = player?.class?.toLowerCase() || '';
        if (playerClass.includes('grad') || playerClass.includes('alumni')) {
            return false;
        }
        return true;
    }
    let yearNum;
    if (typeof playerYear === 'string') {
        const yearMatch = playerYear.match(/(\d{4})/);
        yearNum = yearMatch ? parseInt(yearMatch[1], 10) : NaN;
    } else {
        yearNum = playerYear;
    }
    if (isNaN(yearNum)) return true;
    return yearNum >= currentSeason - 1;
};

const MyPlayerProfileCard = ({ searchQuery = '', teamFilter = false, activePosition = '', viewMode = 'grid', setViewMode, showFormerPlayers = false, onNilPreview }) => {
    const { theme } = useTheme();
    const navigate = useNavigate()
    const isDarkMode = true; // OLED: always dark
    const [selectedTeam, setSelectedTeam] = useState(null);
    // const [teamFilter, setTeamFilter] = useState(false);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeChatPlayer, setActiveChatPlayer] = useState(null);
    const [allData, setAllData] = useState([])
    // Gemini evaluation state
    const [evaluatingPlayerId, setEvaluatingPlayerId] = useState(null);
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [evaluationError, setEvaluationError] = useState(null);

    // Handle Gemini 2.5-pro player evaluation
    const handleEvaluatePlayer = async (player) => {
        const playerId = player.player_id || player.mlbId || player.id;
        setEvaluatingPlayerId(playerId);
        setEvaluationError(null);
        setEvaluationResult(null);

        try {
            const sport = getSelectedSport();
            const result = await evaluatePlayer({
                player: {
                    name: player.name || `${player.first_name} ${player.last_name}`,
                    position: player.position,
                    height: player.height,
                    weight: player.weight,
                    team: selectedTeam?.full_name || selectedTeam?.team_name,
                    stats: player.stats || {},
                    rating: player.slam_score || player.SLAM_Score,
                },
                sport,
            });
            setEvaluationResult({ playerId, text: result, playerName: player.name || `${player.first_name} ${player.last_name}` });
        } catch (error) {
            console.error('Evaluation error:', error);
            setEvaluationError('Unable to generate evaluation. Please try again.');
        } finally {
            setEvaluatingPlayerId(null);
        }
    };
    // Load selected team from session storage on mount
    useEffect(() => {
        const savedTeam = sessionStorage.getItem('selectedTeam');
        if (savedTeam) {
            setSelectedTeam(JSON.parse(savedTeam));
        }
    }, []);

    // Listen for team changes from Settings and refresh data
    useEffect(() => {
        const handleTeamChange = () => {
            console.log('Team changed event received in MyPlayerProfileCard, refreshing...');
            const savedTeam = sessionStorage.getItem('selectedTeam');
            if (savedTeam) {
                const newTeam = JSON.parse(savedTeam);
                setSelectedTeam(newTeam);
                // Refresh team players with new team
                if (teamFilter && newTeam?.team_id) {
                    fetchTeamPlayers(newTeam.team_id);
                }
            }
        };

        window.addEventListener('teamChange', handleTeamChange);
        return () => window.removeEventListener('teamChange', handleTeamChange);
    }, [teamFilter]);

    useEffect(() => {
        applyFilters();
    }, [searchQuery, activePosition, showFormerPlayers, allData]);

    useEffect(() => {
        if (!searchQuery?.length) {
            fetchTeamPlayers(selectedTeam?.team_id)
        }
    }, [searchQuery])

    useEffect(() => {
        if (activePosition == 'all') {
            fetchTeamPlayers(selectedTeam?.team_id)
        }
    }, [activePosition])

    const applyFilters = () => {
        let results = [...allData];

        // Filter out former players unless toggle is on
        if (!showFormerPlayers) {
            const currentSeason = getCurrentSeason();
            results = results.filter(player => isCurrentPlayer(player, currentSeason));
        }

        // Apply search query
        if (searchQuery) {
            const query = searchQuery?.toLowerCase();
            results = results?.filter(player => (player?.Player || '').toLowerCase().includes(query) || (player?.name || '').toLowerCase().includes(query));
        }
        if (activePosition !== 'all') {
            results = results.filter(player => player?.Position == activePosition);
        }

        setTeamPlayers(results);
    };

    // Fetch team players when teamFilter becomes true
    useEffect(() => {
        if (teamFilter && selectedTeam) {
            fetchTeamPlayers(selectedTeam?.team_id);
        }
    }, [teamFilter, selectedTeam]);

    const fetchTeamPlayers = async (teamId) => {
        if (!teamId) return;

        setLoading(true);
        setTeamPlayers([])
        try {
            const currentSport = getSelectedSport();
            // For MLB/pro sports, team_id is an abbreviation string (e.g., 'nyy')
            // For college sports, team_id may be numeric or a slug
            const sportsWithStringIds = ['MLB', 'MiLB', 'NPB', 'KBO', 'NBA', 'WNBA'];
            let apiTeamId;
            if (sportsWithStringIds.includes(currentSport)) {
                // Use the team_id as-is for pro sports (it's an abbreviation like 'nyy')
                apiTeamId = teamId;
            } else {
                // For college sports, extract numeric ID if present
                apiTeamId = typeof teamId === 'string' ? (teamId.replace(/\D/g, '') || teamId) : teamId;
            }

            let playersData = [];

            // Use MLB roster service for MLB teams (local cache + MLB API fallback)
            if (currentSport === 'MLB') {
                const mlbRoster = await fetchMLBTeamRoster(apiTeamId);
                console.log('MLB roster from roster service:', mlbRoster);
                playersData = mlbRoster.map(player => ({
                    PlayerID: player.player_id || player.mlbId,
                    Player: player.name,
                    Position: player.position || 'N/A',
                    Height: player.height || 'N/A',
                    Weight: player.weight || 'N/A',
                    PlayerNo: player.number || 'N/A',
                    TeamAbbrev: player.team_abbreviation || selectedTeam?.abbreviation || 'N/A',
                    image: player.image_url || player.imageUrl || player.photo || null,
                    team: player.team || selectedTeam?.name || 'N/A',
                    ERA: player.era ?? 'N/A',
                    AVG: player.batting_average ?? 'N/A',
                    // Include additional MLB stats
                    home_runs: player.home_runs,
                    rbi: player.rbi,
                    ops: player.ops,
                    wins: player.wins,
                    strikeouts: player.strikeouts,
                }));
            } else if (currentSport === 'NBA' || currentSport === 'WNBA') {
                // Use basketball data service for NBA/WNBA
                // Fix: Use team_name (from settings) or name as fallback
                const teamName = selectedTeam?.team_name || selectedTeam?.name || selectedTeam?.full_name;
                console.log(`Fetching ${currentSport} roster for team: "${teamName}"`);
                const basketballPlayers = await getBasketballPlayers(currentSport, teamName);
                console.log('Basketball roster from service:', basketballPlayers);
                playersData = basketballPlayers.map(player => ({
                    PlayerID: player.player_id || player.id,
                    Player: player.player_name || player.name || `${player.firstName} ${player.lastName}`,
                    Position: player.position || 'N/A',
                    Height: player.height || 'N/A',
                    Weight: player.weight || 'N/A',
                    PlayerNo: player.jersey_number || player.number || 'N/A',
                    TeamAbbrev: selectedTeam?.abbreviation || apiTeamId?.toUpperCase() || 'N/A',
                    image: player.headshot?.href || player.image || null,
                    team: player.team || selectedTeam?.name || 'N/A',
                    ppg: player.ppg || player.stats?.ppg || 'N/A',
                    apg: player.apg || player.stats?.apg || 'N/A',
                    rpg: player.rpg || player.stats?.rpg || 'N/A',
                }));
            } else if (currentSport === 'MBB') {
                const response = await getMBBRoasters(selectedTeam?.name);
                if (response?.data) {
                    playersData = Array.isArray(response.data) ? response.data : [];
                    playersData = playersData.map(player => ({
                        PlayerID: player.PlayerID || player.player_id,
                        Player: player.Player || player.name || player.player_name,
                        Position: player.Position || player.position || 'N/A',
                        Height: player.Height || player.height || 'N/A',
                        Weight: player.Weight || player.weight || 'N/A',
                        PlayerNo: player.PlayerNo || player.jersey_number || 'N/A',
                        TeamAbbrev: player.TeamAbbrev || selectedTeam?.abbreviation || 'N/A',
                        image: player.image || player.photo || player.ImageUrl || null,
                        team: player.team_name || player.team || player.school || selectedTeam?.name || 'N/A',
                        ppg: player.core_stats?.PPG ? Number(player.core_stats.PPG).toFixed(2) : 'N/A',
                        apg: player.core_stats?.APG ? Number(player.core_stats.APG).toFixed(2) : 'N/A',
                        rpg: player.core_stats?.RPG ? Number(player.core_stats.RPG).toFixed(2) : 'N/A',
                    }));
                }
            } else if (currentSport === 'CBB') {
                // Use local roster service for college baseball (like MLB)
                const teamName = selectedTeam?.team_name || selectedTeam?.name || selectedTeam?.full_name || apiTeamId;
                const cbbRoster = getCBBTeamRoster(teamName);
                console.log('CBB roster from local service:', cbbRoster);
                playersData = cbbRoster.map(player => ({
                    PlayerID: player.player_id || player.id,
                    Player: player.name || player.Player,
                    Position: player.position || player.Position || 'N/A',
                    Height: player.height || player.Height || 'N/A',
                    Weight: player.weight || player.Weight || 'N/A',
                    PlayerNo: player.number || player.PlayerNo || 'N/A',
                    TeamAbbrev: player.team_abbreviation || player.TeamAbbrev || selectedTeam?.abbreviation || 'N/A',
                    image: player.image_url || player.imageUrl || player.photo || player.image || null,
                    team: player.team || selectedTeam?.name || 'N/A',
                    year: player.year || player.class || 'N/A',
                    ERA: player.ERA ?? player.era ?? 'N/A',
                    AVG: player.AVG ?? player.batting_average ?? 'N/A',
                    // Include additional baseball stats
                    home_runs: player.home_runs,
                    rbi: player.rbi,
                    ops: player.ops,
                    wins: player.wins,
                    strikeouts: player.strikeouts,
                }));
            } else {
                // For other sports, use API
                const response = await apiClient.get(`${currentSport}/teams/${apiTeamId}/players`);
                console.log('API response status 01:', response);

                if (response.data) {
                    playersData = Array.isArray(response.data) ? response.data : [];
                    playersData = playersData.map(player => ({
                        PlayerID: player.PlayerID || player.player_id,
                        Player: player.Player || player.name || player.player_name,
                        Position: player.Position || player.position || 'N/A',
                        Height: player.Height || player.height || 'N/A',
                        Weight: player.Weight || player.weight || 'N/A',
                        PlayerNo: player.PlayerNo || player.jersey_number || 'N/A',
                        TeamAbbrev: player.TeamAbbrev || selectedTeam?.abbreviation || 'N/A',
                        image: player.image || player.photo || player.ImageUrl || null,
                        team: player.team_name || player.team || player.school || selectedTeam?.name || 'N/A',
                        season: player.season || player.roster_year || player.year || player.academic_year,
                        year: player.year || player.class,
                        class: player.class || player.year,
                        ERA: player.ERA ?? player.stats?.pitching?.era?.toFixed(2) ?? 'N/A',
                        ppg: player.core_stats?.PPG ? Number(player.core_stats.PPG).toFixed(2) : 'N/A',
                        apg: player.core_stats?.APG ? Number(player.core_stats.APG).toFixed(2) : 'N/A',
                        rpg: player.core_stats?.RPG ? Number(player.core_stats.RPG).toFixed(2) : 'N/A',
                        AVG: player.AVG ?? player.stats?.batting?.avg?.toFixed(3) ?? 'N/A'
                    }));
                }
            }

            console.log(`Found ${playersData.length} players`, playersData);

            setTeamPlayers(playersData);
            setAllData(playersData)

            // Publish an event so other components can use this data
            const teamPlayersEvent = new CustomEvent('teamPlayersLoaded', {
                detail: { players: playersData, team: selectedTeam }
            });
            window.dispatchEvent(teamPlayersEvent);

        } catch (error) {
            console.error('Error fetching team players:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!selectedTeam) {
        return null; // Don't show anything if no team is selected
    }
    const getPositionFullName = (position) => {
        // Basketball sports use basketball positions, baseball sports use baseball positions
        const basketballSports = ['NBA', 'WNBA', 'MBB', 'WBB'];
        const positions = basketballSports.includes(getSelectedSport()) ? positionsMBB : positionsBaseball;
        // Find the matching position in the list
        const found = positions.find(pos => pos.id === position);
        return found ? found?.label?.toUpperCase() : '-';
    };

    return (
        <div className="my-4 mt-0 mb-0">
            {loading && (
                <div className="w-full">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 3 }).map((_, index) => (
                            <PlayerCardSkeleton key={index} />
                        ))}
                    </div>
                </div>
            )}

            {/* Display team players when filter is active - Modernized 2026 Grid */}
            {teamFilter && teamPlayers.length > 0 && viewMode === 'grid' && (
                <>
                    <div className="w-full flex justify-end mb-4">
                        <span
                            className="text-sm font-semibold"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                        >
                            {teamPlayers?.length} players found
                        </span>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {teamPlayers.map((player, index) => {
                            const playerName = player.Player || player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim();
                            let playerPosition = player.Position || player.position || player.position_abbreviation || '';
                            const playerHeight = player.Height || player.height || '';
                            const playerWeight = player.Weight || player.weight || '';
                            if (!playerPosition) playerPosition = "-";

                            return (
                                <div
                                    key={player.PlayerID || player.id || index}
                                    className="rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                                    }}
                                >
                                    <div className="p-6">
                                        {/* Player Header */}
                                        <div className="flex items-start gap-4 mb-6">
                                            {/* Player Avatar */}
                                            <div
                                                className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.2) 0%, rgba(255,165,0,0.1) 100%)',
                                                    border: '2px solid rgba(232,184,0,0.3)',
                                                    boxShadow: '0 4px 16px rgba(232,184,0,0.2)',
                                                }}
                                            >
                                                <img
                                                    src={player.image || player.photo || player.ImageUrl || `/SLAM_logo.png`}
                                                    alt={playerName}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "/SLAM_logo.png";
                                                        e.target.classList.add("p-2");
                                                        e.target.style.objectFit = "contain";
                                                    }}
                                                />
                                            </div>

                                            {/* Player Info */}
                                            <div className="flex-1 min-w-0">
                                                <h3
                                                    className="text-lg font-bold mb-1 leading-tight"
                                                    style={{ color: '#fff' }}
                                                >
                                                    {playerName}
                                                </h3>
                                                <div
                                                    className="text-sm mb-2"
                                                    style={{ color: 'rgba(255,255,255,0.5)' }}
                                                >
                                                    {playerHeight ? playerHeight : 'N/A'} • {playerWeight ? playerWeight : 'N/A'}
                                                </div>
                                                <div
                                                    className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold"
                                                    style={{
                                                        background: 'rgba(232,184,0,0.15)',
                                                        color: '#E8B800',
                                                    }}
                                                >
                                                    {playerPosition && playerPosition !== '-' ? playerPosition : 'N/A'}
                                                </div>
                                                {player.team && player.team !== 'N/A' && (
                                                    <div className="text-xs font-semibold mt-2" style={{ color: '#E8B800' }}>
                                                        {player.team}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Stats Row - Sport-specific */}
                                        <div
                                            className="flex justify-around py-4 mb-3 rounded-xl"
                                            style={{
                                                background: 'rgba(255,255,255,0.03)',
                                            }}
                                        >
                                            {(() => {
                                                const currentSport = getSelectedSport();
                                                const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(playerPosition);

                                                // Baseball stats for MLB and CBB (College Baseball)
                                                if (currentSport === 'MLB' || currentSport === 'CBB') {
                                                    if (isPitcher) {
                                                        return [
                                                            { label: 'ERA', value: player.era || player.ERA, color: '#22c55e' },
                                                            { label: 'WHIP', value: player.whip || player.WHIP, color: '#3b82f6' },
                                                            { label: 'K', value: player.strikeouts, color: '#f59e0b' },
                                                            { label: 'W-L', value: player.wins != null ? `${player.wins}-${player.losses || 0}` : null, color: '#a855f7' }
                                                        ];
                                                    } else {
                                                        return [
                                                            { label: 'AVG', value: player.batting_average || player.AVG, color: '#22c55e' },
                                                            { label: 'OPS', value: player.ops || player.OPS, color: '#3b82f6' },
                                                            { label: 'HR', value: player.home_runs, color: '#f59e0b' },
                                                            { label: 'RBI', value: player.rbi, color: '#a855f7' }
                                                        ];
                                                    }
                                                }
                                                // Basketball stats (default)
                                                return [
                                                    { label: 'PPG', value: player.ppg, color: '#22c55e' },
                                                    { label: 'RPG', value: player.rpg, color: '#3b82f6' },
                                                    { label: 'APG', value: player.apg, color: '#f59e0b' }
                                                ];
                                            })().map((stat, i) => (
                                                <div key={i} className="text-center">
                                                    <div
                                                        className="text-xl font-bold"
                                                        style={{ color: stat.color || ('#fff') }}
                                                    >
                                                        {stat.value ?? 'N/A'}
                                                    </div>
                                                    <div
                                                        className="text-[10px] font-semibold uppercase tracking-wider"
                                                        style={{ color: 'rgba(255,255,255,0.4)' }}
                                                    >
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Statcast/Advanced Metrics Row - Baseball only */}
                                        {(getSelectedSport() === 'MLB' || getSelectedSport() === 'CBB') && (
                                            <div
                                                className="flex justify-around py-2 mb-3 rounded-lg border"
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                                                    borderColor: 'rgba(59,130,246,0.2)',
                                                }}
                                            >
                                                {(() => {
                                                    const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(playerPosition);
                                                    if (isPitcher) {
                                                        return [
                                                            { label: 'Velo', value: player.avgFastball ? `${player.avgFastball}` : null, icon: <Zap className="w-3 h-3" /> },
                                                            { label: 'Spin', value: player.spinRate, icon: <Activity className="w-3 h-3" /> },
                                                            { label: 'Whiff%', value: player.whiffRate ? `${player.whiffRate}%` : null, icon: <Target className="w-3 h-3" /> },
                                                        ];
                                                    } else {
                                                        return [
                                                            { label: 'Exit Velo', value: player.exitVelocity ? `${player.exitVelocity}` : null, icon: <Zap className="w-3 h-3" /> },
                                                            { label: 'LA', value: player.launchAngle ? `${player.launchAngle}°` : null, icon: <TrendingUp className="w-3 h-3" /> },
                                                            { label: 'Barrel%', value: player.barrelRate ? `${player.barrelRate}%` : null, icon: <Target className="w-3 h-3" /> },
                                                        ];
                                                    }
                                                })().map((stat, i) => (
                                                    <div key={i} className="text-center flex flex-col items-center">
                                                        <div className="flex items-center gap-1">
                                                            <span style={{ color: '#60a5fa' }}>{stat.icon}</span>
                                                            <span
                                                                className="text-sm font-semibold"
                                                                style={{ color: '#93c5fd' }}
                                                            >
                                                                {stat.value ?? '-'}
                                                            </span>
                                                        </div>
                                                        <div
                                                            className="text-[9px] uppercase tracking-wider"
                                                            style={{ color: 'rgba(147,197,253,0.6)' }}
                                                        >
                                                            {stat.label}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Health/Biometric Integration Row */}
                                        <div className="mb-4">
                                            <BiometricBadgesCompact
                                                playerId={player.PlayerID || player.mlbId || player.player_id || index}
                                            />
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                onClick={() => {
                                                    const playerId = player.PlayerID || player.id || index;
                                                    const path = getSelectedSport() === 'MBB'
                                                        ? `/team-player-profile/${playerName}`
                                                        : `/player/${playerId}`;
                                                    navigate(path, { state: { playerData: player } });
                                                }}
                                                className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105"
                                                style={{
                                                    background: 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)',
                                                    color: '#000',
                                                    boxShadow: '0 4px 16px rgba(232,184,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                                }}
                                            >
                                                Profile
                                            </button>

                                            {onNilPreview && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onNilPreview({
                                                            ...player,
                                                            name: playerName,
                                                            position: playerPosition,
                                                            height: playerHeight,
                                                            weight: playerWeight
                                                        });
                                                    }}
                                                    className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C00 100%)',
                                                        color: '#000',
                                                        boxShadow: '0 4px 16px rgba(255,107,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    <Smartphone className="w-4 h-4" />
                                                    NIL
                                                </button>
                                            )}
                                            {/* AI Scout Evaluation Button - Grid View */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEvaluatePlayer(player);
                                                }}
                                                disabled={evaluatingPlayerId === (player.player_id || player.mlbId || player.id || index)}
                                                className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-wait"
                                                style={{
                                                    background: evaluatingPlayerId === (player.player_id || player.mlbId || player.id || index)
                                                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                                                        : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                                    color: '#fff',
                                                    boxShadow: '0 4px 16px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                                }}
                                                title="AI Scout Evaluation (Gemini 2.5-pro)"
                                            >
                                                {evaluatingPlayerId === (player.player_id || player.mlbId || player.id || index) ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Sparkles className="w-4 h-4" />
                                                )}
                                                {evaluatingPlayerId === (player.player_id || player.mlbId || player.id || index) ? 'Evaluating...' : 'AI Scout'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {/* List view - Modernized 2026 Glassmorphism */}
            {teamFilter && teamPlayers.length > 0 && viewMode === 'list' && (
                <div
                    className="rounded-2xl overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                    }}
                >
                    {/* Players Count Header */}
                    <div
                        className="px-6 py-4 flex items-center justify-between"
                        style={{
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}
                    >
                        <span
                            className="text-sm font-semibold"
                            style={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                            {teamPlayers?.length} players found
                        </span>
                    </div>

                    {/* Table Header */}
                    <div
                        className="grid grid-cols-12 gap-4 px-6 py-4"
                        style={{
                            background: 'rgba(255,255,255,0.02)',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}
                    >
                        <div className="col-span-4 text-xs font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Player
                        </div>
                        <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Position
                        </div>
                        <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Height/Weight
                        </div>
                        <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Stats
                        </div>
                        <div className="col-span-2 text-xs font-bold uppercase tracking-wider text-center" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Actions
                        </div>
                    </div>

                    {/* Table Body */}
                    <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                        {teamPlayers.map((player, index) => {
                            const playerName = player.Player || player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim();
                            let playerPosition = player.Position || player.position || player.position_abbreviation || '';
                            const playerHeight = player.Height || player.height || '';
                            const playerWeight = player.Weight || player.weight || '';

                            return (
                                <div
                                    key={player.PlayerID || player.id || index}
                                    className="grid grid-cols-12 gap-4 px-6 py-5 items-center transition-all duration-200 cursor-pointer"
                                    style={{
                                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(232,184,0,0.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    {/* Player Info */}
                                    <div className="col-span-4 flex items-center gap-4">
                                        <div
                                            className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
                                            style={{
                                                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                            }}
                                        >
                                            <img
                                                src={player.image || player.photo || player.ImageUrl || `/SLAM_logo.png`}
                                                alt={playerName}
                                                className="w-full h-full object-cover object-top"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "/SLAM_logo.png";
                                                    e.target.classList.add("p-2");
                                                    e.target.style.objectFit = "contain";
                                                }}
                                            />
                                        </div>
                                        <div
                                            onClick={() => {
                                                const playerId = player.PlayerID || player.id || index;
                                                const path = getSelectedSport() === 'MBB'
                                                    ? `/team-player-profile/${playerName}`
                                                    : `/player/${playerId}`;
                                                navigate(path, { state: { playerData: player } });
                                            }}
                                        >
                                            <div
                                                className="font-semibold text-sm"
                                                style={{ color: '#fff' }}
                                            >
                                                {playerName}
                                            </div>
                                            {player.team && player.team !== 'N/A' && (
                                                <div className="text-xs font-medium" style={{ color: '#E8B800' }}>
                                                    {player.team}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Position */}
                                    <div className="col-span-2 text-center">
                                        <span
                                            className="text-sm font-medium"
                                            style={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            {playerPosition && playerPosition !== '-' ? playerPosition : 'N/A'}
                                        </span>
                                    </div>

                                    {/* Height/Weight */}
                                    <div className="col-span-2 text-center">
                                        <span
                                            className="text-sm font-medium"
                                            style={{ color: 'rgba(255,255,255,0.6)' }}
                                        >
                                            {playerHeight ? playerHeight : 'N/A'} • {playerWeight ? playerWeight : 'N/A'}
                                        </span>
                                    </div>

                                    {/* Stats - Sport-specific */}
                                    <div className="col-span-2 flex justify-center gap-2">
                                        {(() => {
                                            const currentSport = getSelectedSport();
                                            const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(playerPosition);

                                            // Baseball stats for MLB and CBB (College Baseball)
                                            if (currentSport === 'MLB' || currentSport === 'CBB') {
                                                if (isPitcher) {
                                                    return [
                                                        { label: 'ERA', value: player.era || player.ERA },
                                                        { label: 'W', value: player.wins },
                                                        { label: 'K', value: player.strikeouts }
                                                    ];
                                                } else {
                                                    return [
                                                        { label: 'AVG', value: player.batting_average || player.AVG },
                                                        { label: 'HR', value: player.home_runs },
                                                        { label: 'RBI', value: player.rbi }
                                                    ];
                                                }
                                            }
                                            // Basketball stats (default)
                                            return [
                                                { label: 'PPG', value: player.ppg },
                                                { label: 'RPG', value: player.rpg },
                                                { label: 'APG', value: player.apg }
                                            ];
                                        })().map((stat, i) => (
                                            <span
                                                key={i}
                                                className="px-2 py-1 rounded-lg text-xs font-semibold"
                                                style={{
                                                    background: 'rgba(255,255,255,0.08)',
                                                    color: 'rgba(255,255,255,0.8)',
                                                }}
                                            >
                                                {stat.label}: {stat.value || 'N/A'}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Actions */}
                                    <div className="col-span-2 flex justify-center gap-2">
                                        <button
                                            onClick={() => {
                                                const playerId = player.PlayerID || player.id || index;
                                                const path = getSelectedSport() === 'MBB'
                                                    ? `/team-player-profile/${playerName}`
                                                    : `/player/${playerId}`;
                                                navigate(path, { state: { playerData: player } });
                                            }}
                                            className="px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200 hover:scale-105"
                                            style={{
                                                background: 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)',
                                                color: '#000',
                                                boxShadow: '0 2px 8px rgba(232,184,0,0.25)',
                                            }}
                                        >
                                            Profile
                                        </button>
                                        {onNilPreview && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onNilPreview({
                                                        ...player,
                                                        name: playerName,
                                                        position: playerPosition,
                                                        height: playerHeight,
                                                        weight: playerWeight
                                                    });
                                                }}
                                                className="px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-1"
                                                style={{
                                                    background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C00 100%)',
                                                    color: '#000',
                                                    boxShadow: '0 2px 8px rgba(255,107,0,0.25)',
                                                }}
                                                title="NIL Card Preview"
                                            >
                                                <Smartphone className="w-3.5 h-3.5" />
                                                NIL
                                            </button>
                                        )}
                                        {/* Gemini 2.5-pro Player Evaluation Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEvaluatePlayer(player);
                                            }}
                                            disabled={evaluatingPlayerId === (player.PlayerID || player.id || index)}
                                            className="px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200 hover:scale-105 flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
                                            style={{
                                                background: evaluatingPlayerId === (player.PlayerID || player.id || index)
                                                    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                                                    : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                                color: '#fff',
                                                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                                            }}
                                            title="AI Scout Evaluation (Gemini 2.5-pro)"
                                        >
                                            {evaluatingPlayerId === (player.PlayerID || player.id || index) ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-3.5 h-3.5" />
                                            )}
                                            {evaluatingPlayerId === (player.PlayerID || player.id || index) ? 'Evaluating...' : 'AI Scout'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {teamFilter && teamPlayers.length === 0 && !loading && (
                <div className={`mt-6 pt-6 border-t ${'border-gray-700'}`}>
                    <div className="flex items-center mb-4">
                        <div className="w-1 h-8 mr-3 rounded themeSecondary" style={selectedTeam?.team_color ? { backgroundColor: selectedTeam?.team_color } : {}}></div>
                        <h2 className={`text-2xl font-bold ${'text-gray-200'}`}>{selectedTeam?.full_name || selectedTeam?.team_name} Roster</h2>
                    </div>
                    <div className={`bg-gray-100 rounded-lg p-6 text-center ${'bg-gray-800'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-12 w-12 mx-auto ${'text-gray-500'} mb-4`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className={`text-lg font-medium ${'text-gray-200'}`}>No player data available</p>
                        <p className={`${'text-gray-400'} mt-2`}>We couldn't find any players for this team.</p>
                    </div>
                </div>
            )}
            {activeChatPlayer && (
                <PlayerChatbot key={activeChatPlayer} setActiveChatPlayer={setActiveChatPlayer} name={activeChatPlayer} defaultOpened={true} />
            )}

            {/* AI Scout Evaluation Modal */}
            {(evaluationResult || evaluationError) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <div
                        className="relative max-w-md w-full rounded-2xl p-6"
                        style={{
                            background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                        }}
                    >
                        {/* Close button */}
                        <button
                            onClick={() => {
                                setEvaluationResult(null);
                                setEvaluationError(null);
                            }}
                            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-500/20 transition-colors"
                        >
                            <X className={`w-5 h-5 ${'text-gray-400'}`} />
                        </button>

                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                style={{
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                }}
                            >
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className={`font-bold ${'text-white'}`}>
                                    AI Scout Evaluation
                                </h3>
                                <p className={`text-xs ${'text-gray-400'}`}>
                                    Powered by Gemini 2.5-pro
                                </p>
                            </div>
                        </div>

                        {/* Player name */}
                        {evaluationResult?.playerName && (
                            <p className={`text-sm font-semibold mb-3 ${'text-yellow-400'}`}>
                                {evaluationResult.playerName}
                            </p>
                        )}

                        {/* Content */}
                        {evaluationError ? (
                            <p className="text-red-500 text-sm">{evaluationError}</p>
                        ) : (
                            <p className={`text-sm leading-relaxed ${'text-gray-300'}`}>
                                {evaluationResult?.text}
                            </p>
                        )}

                        {/* Footer */}
                        <div className="mt-6 pt-4 border-t border-gray-700/30">
                            <p className={`text-xs ${'text-gray-500'}`}>
                                This evaluation is AI-generated and should be used as a starting point for analysis.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyPlayerProfileCard;